import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useContext,
} from "react";
import { useMediaQuery } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { append, equals, join, map, mergeRight, reject, toLower } from "ramda";
import { useSearchQuery } from "../../common/use-searchQuery";
import {
  contentStatus,
  IncludeDialogTypes,
  isApproved,
  isApprovedOrPending,
  isPending,
  useView,
} from "../organizeUtil";
import { useContentUpdate } from "../../content/contentServices";
import { useContentByStatus, pendingQueryOptions } from "../../creatorServices";
import { contentTypeTerms, getContentTypeLabel } from "../../contentUtils";
import { isBlank, notBlank } from "../../../util/ext";
import { useSelected } from "../OrganizeProvider";
import { capitalize } from "../../../util/stringFormat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faRotateRight } from "@fortawesome/pro-solid-svg-icons";
import { faCopy } from "@fortawesome/pro-regular-svg-icons";
import { useLocation } from "react-router-dom";
import { OrganizeContext } from "../organize-context";

import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import EditSearchModal from "../../common/EditSearchModal";
import ContentSearchBar from "../../content/browse/ContentSearchBar";
import ContentSourcesAutocomplete from "../common/ContentSourcesAutocomplete";
import ContentTypeFiltersMenu from "./ContentTypeFiltersMenu";
import MediaBrowser from "../../common/MediaBrowser";
import OrganizeReviewTable from "./OrganizeReviewTable";
import QueryPills from "../../common/QueryPills";
import SelectedFilesCheckbox from "../files/SelectedFilesCheckbox";
import { useQueryClient } from "react-query";

const initialSearchState = {
  appliedQuery: [],
  limit: 10,
  orderBy: "media.auditUpdated",
  orderDirection: "desc",
  page: 0,
  q: "",
  source: {
    projectName: "",
    projectKey: "",
  },
  types: [],
};

const getInitialSearchState = initialState =>
  mergeRight(initialSearchState, initialState);

// const getContentTypeTerms = map(ct => ({ label: getContentTypeLabel(ct) }));

const useStyles = makeStyles(theme => ({
  approve: {
    color: theme.palette.approved,
    fontWeight: "bold",
  },

  icon: {
    "& > :first-child": {
      fontSize: "1.4rem",
    },
  },

  refresh: {
    color: "black",
  },

  titleActions: {
    height: 50,
    paddingTop: 10,
    [theme.breakpoints.up("md")]: {
      paddingTop: 0,
    },
  },
}));

const OrganizeReviewBrowser = props => {
  const view = useView();
  const classes = useStyles();
  const location = useLocation();
  const isXs = useMediaQuery(theme => theme.breakpoints.down("sm"));
  const betweenSmAndMed = useMediaQuery(
    "(min-width:1300px) and (max-width:1440px) ",
  );

  const { updateIncludeDialog } = useContext(OrganizeContext);
  const { removeAllSelectedContent, selectedContent } = useSelected();
  const { SnackAlert, showAlert } = props.SnackAlertHandler;

  const openIncludeDialog = action => event => {
    updateIncludeDialog({
      open: true,
      action: action,
      activeType: IncludeDialogTypes.content,
      selected: selectedContent,
    });
  };

  const [scrollVisible, setScrollVisible] = useState(true);
  const [status, setStatus] = useState("pending");

  //-- Handle Search State
  const initialSearchState = getInitialSearchState(
    notBlank(location.state) ? location.state.searchState : {},
  );
  const searchQuery = useSearchQuery(initialSearchState);
  const { handleUpdatePage } = searchQuery;

  const [searchState, setSearchState] = useState({
    ...initialSearchState,
    status,
    type: join(",", searchQuery.state.types),
    offset: searchQuery.state.limit * searchQuery.state.page,
    sort: `${searchQuery.state.orderBy} ${searchQuery.state.orderDirection}`,
    source: searchQuery.state.source,
  });

  // refresh processing query when switching to review browser
  const queryClient = useQueryClient();
  useEffect(() => {
    if (notBlank(view)) {
      setStatus(contentStatus[view]);
      handleUpdatePage(0);
      queryClient.invalidateQueries(["content-processing"]);
    }
  }, [view, handleUpdatePage, queryClient]);

  const handleRefresh = () => {
    searchQuery.reset();
    contentResults.refetch();
  };

  useEffect(() => {
    const { limit, page } = searchQuery.state;

    const sort =
      searchQuery.state.orderBy === null
        ? `${initialSearchState.orderBy} ${initialSearchState.orderDirection}`
        : `${searchQuery.state.orderBy} ${searchQuery.state.orderDirection}`;

    setSearchState({
      ...searchQuery.state,
      status,
      sort,
      offset: limit * page,
      type: join(",", searchQuery.state.types),
      q: join(" OR ", searchQuery.state.appliedQuery),
    });

    setScrollVisible(true);
  }, [
    searchQuery.state,
    status,
    initialSearchState.orderBy,
    initialSearchState.orderDirection,
  ]);

  const contentResults = useContentByStatus(
    {
      ...searchState,
      source: searchState.source.projectKey,
    },
    equals(toLower(status), "pending") ? pendingQueryOptions : {},
  );
  const [count, setCount] = useState("-");
  useEffect(() => {
    if (contentResults.isSuccess) {
      setCount(contentResults.data.count);
    }

    if (contentResults.isLoading) {
      setCount("-");
    }
  }, [contentResults.isLoading, contentResults.isSuccess, contentResults.data]);

  const handleContentTypeSelected = term => {
    searchQuery.handleUpdateTypeSelect(term);
    removeAllSelectedContent();
  };

  const handleClearContentType = () => {
    searchQuery.handleUpdateTypes([]);
    removeAllSelectedContent();
  };

  const handleScrollVisible = useCallback(visible => {
    setScrollVisible(visible);
  }, []);

  const updateContent = useContentUpdate();
  const handleStatusUpdate = newStatus => async () => {
    try {
      await Promise.all(
        map(
          content =>
            updateContent.mutateAsync({
              resourceIds: { id: content.id },
              data: {
                status: newStatus,
              },
            }),
          selectedContent,
        ),
      );

      showAlert("success", [`Item(s) ${capitalize(newStatus)}`]);
      removeAllSelectedContent();
    } catch (err) {
      showAlert("error", [err.message]);
    }
  };

  //-- Replace Search Term in Query Pill
  const [editSearchModal, setEditSearchModal] = useState(false);
  const [previousSearchTerm, setPreviousSearchTerm] = useState("");
  const closeSearchModal = () => setEditSearchModal(false);
  const handleQueryPillClick = queryPart => {
    setEditSearchModal(true);
    setPreviousSearchTerm(queryPart);
  };
  const handleFilterDelete = useCallback(
    value => {
      searchQuery.handleUpdateQuery(
        reject(equals(value), searchQuery.state.appliedQuery),
      );
    },
    [searchQuery],
  );

  const handleEditSearchApply = useCallback(
    newSearchTerm => {
      const appliedQuery = searchQuery.state.appliedQuery;

      const withoutPreviousTerm = reject(
        equals(previousSearchTerm),
        appliedQuery,
      );

      const updatedQuery = isBlank(newSearchTerm)
        ? withoutPreviousTerm
        : append(newSearchTerm, withoutPreviousTerm);

      searchQuery.handleUpdateQuery(updatedQuery);

      closeSearchModal();
      setPreviousSearchTerm("");
    },
    [previousSearchTerm, searchQuery],
  );

  return (
    <Fragment>
      <SnackAlert />

      <EditSearchModal
        editSearchModal={editSearchModal}
        previousSearchTerm={previousSearchTerm}
        handleEditSearchModalClose={closeSearchModal}
        handleEditSearchApply={handleEditSearchApply}
      />

      <MediaBrowser
        loaded={contentResults.isSuccess}
        scrollVisible={scrollVisible}
        searchBar={
          <Grid
            container
            justify="space-between"
            alignItems={isXs ? "flex-start" : "center"}
            style={{ padding: "15px 0" }}
          >
            <Grid
              item
              container
              xs={6}
              alignItems="center"
              className={classes.titleActions}
            >
              <Grid item>
                <Typography variant="h2" style={{ height: 40, paddingTop: 7 }}>
                  {capitalize(view)} ({count})
                </Typography>
              </Grid>

              {isPending(view) && (
                <Grid item>
                  <Tooltip title="Refresh">
                    <IconButton
                      onClick={handleRefresh}
                      classes={{ root: classes.refresh }}
                    >
                      <FontAwesomeIcon icon={faRotateRight} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}

              {contentResults.isSuccess && notBlank(selectedContent) && (
                <Fragment>
                  {isApprovedOrPending(view) && (
                    <Grid item>
                      <SelectedFilesCheckbox
                        isPagedContent
                        total={contentResults.data.count}
                        content={contentResults.data.contents}
                      />
                    </Grid>
                  )}

                  {isApproved(view) && (
                    <Grid item>
                      <Tooltip title="Copy">
                        <IconButton onClick={openIncludeDialog("copy")}>
                          <FontAwesomeIcon icon={faCopy} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  )}

                  {isPending(view) && (
                    <Grid item>
                      <Tooltip title="Approve All Selected">
                        <Button
                          classes={{
                            root: classes.approve,
                            iconSizeMedium: classes.icon,
                          }}
                          onClick={handleStatusUpdate("APPROVED")}
                          startIcon={<FontAwesomeIcon icon={faCircleCheck} />}
                        >
                          Approve All
                        </Button>
                      </Tooltip>
                    </Grid>
                  )}
                </Fragment>
              )}
            </Grid>

            <Grid item container xs={6} justify="flex-end" alignItems="center">
              <Grid item xs={12} sm={betweenSmAndMed ? "auto" : 12} md={6}>
                <ContentSearchBar
                  query={searchQuery.state.appliedQuery}
                  onQueryChange={searchQuery.handleUpdateQuery}
                />
              </Grid>
              <Grid item xs="auto" style={{ paddingLeft: 10 }}>
                <ContentSourcesAutocomplete
                  setSource={searchQuery.handleUpdateSource}
                  source={searchQuery.state.source}
                />
              </Grid>
              <Grid item xs="auto">
                <ContentTypeFiltersMenu
                  loading={contentResults.isLoading}
                  contentTypes={contentTypeTerms}
                  contentTypeTerms={map(
                    type => ({ label: getContentTypeLabel(type) }),
                    searchQuery.state.types,
                  )}
                  onContentTypeSelected={handleContentTypeSelected}
                  onClear={handleClearContentType}
                />
              </Grid>
            </Grid>

            <Grid container item alignItems="center">
              <QueryPills
                appliedQuery={searchQuery.state.appliedQuery}
                handleClick={handleQueryPillClick}
                handleDelete={handleFilterDelete}
              />
            </Grid>
          </Grid>
        }
        mediaTable={
          <OrganizeReviewTable
            searchState={searchState}
            sourceFilter={searchQuery.state.source}
            loading={
              updateContent.isLoading ||
              contentResults.isLoading ||
              contentResults.isFetching
            }
            SnackAlertHandler={props.SnackAlertHandler}
            currentStatus={status}
            contentList={
              contentResults.isSuccess ? contentResults.data.contents : []
            }
            total={contentResults.isSuccess ? contentResults.data.count : 0}
            page={searchQuery.state.page}
            orderBy={searchQuery.state.orderBy}
            order={searchQuery.state.orderDirection}
            onSortChange={searchQuery.handleUpdateSort}
            onPageChange={searchQuery.handleUpdatePage}
            onRowsPerPageChange={searchQuery.handleUpdateLimit}
            onScrollVisible={handleScrollVisible}
            rowsPerPage={searchQuery.state.limit}
          />
        }
      />
    </Fragment>
  );
};

export default OrganizeReviewBrowser;
